import { Inject, Injectable, Injector, InjectionToken } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor
} from '@angular/common/http';

import { Observable, throwError, TimeoutError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { timeout } from 'rxjs/operators';
import { ToastService } from '../services/toast.service';

export const DEFAULT_TIMEOUT = new InjectionToken<number>('600000');

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private toastService: ToastService | null = null;

  constructor(private injector: Injector, @Inject(DEFAULT_TIMEOUT) protected defaultTimeout: number) { }

  private get toast(): ToastService {
    if (!this.toastService) {
      this.toastService = this.injector.get(ToastService);
    }
    return this.toastService;
  }

  /**
   * Grocery list, restaurant finder, and invite-user use their own error messages (invite-user
   * surfaces the raw backend string, e.g. "Email already exist"). Skip the global toast for
   * these so we don't show duplicate or generic messages.
   */
  private isOptionalFeatureRequest(url: string | undefined): boolean {
    if (!url) return false;
    return url.includes('grocery-list') || url.includes('restaurants-finder') || url.includes('inviteuser');
  }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const requestUrl = request.url;

    return next.handle(request).pipe(
      timeout(this.defaultTimeout),
      catchError(error => {
        if (error) {
          console.log(error);
          const errorUrl = error?.url ?? requestUrl;
          const skipGlobalToast = this.isOptionalFeatureRequest(errorUrl);

          // TimeoutError from RxJS timeout() has no .status
          if (error instanceof TimeoutError) {
            if (!skipGlobalToast) {
              this.toast.errorMessage('Request timed out. Please try again.');
            }
            return throwError(() => error);
          }

          switch (error.status) {
            case 400:
              if (error.error?.errors) {
                const messages: string[] = [];
                for (const key in error.error.errors) {
                  const val = error.error.errors[key];
                  if (val) {
                    const part = Array.isArray(val) ? val.join(' ') : String(val);
                    if (part) messages.push(part);
                  }
                }
                const msg = messages.length ? messages.join(' ') : (error.error?.message || 'Invalid request.');
                if (!skipGlobalToast) this.toast.errorMessage(msg);
              } else if (error.error?.message && !skipGlobalToast) {
                this.toast.errorMessage(error.error.message);
              } else if (!skipGlobalToast) {
                this.toast.errorMessage('Invalid request. Please check your input.');
              }
              break;
            case 401:
            case 409:
              break;
            case 404:
              if (!skipGlobalToast) {
                this.toast.errorMessage('Resource not found.');
              }
              break;
            case 502:
            case 503:
            case 504:
              if (!skipGlobalToast) {
                this.toast.errorMessage('Service temporarily unavailable. Please try again later.');
              }
              break;
            case 500:
            default:
              if (!skipGlobalToast) {
                this.toast.errorMessage('Something went wrong. Please try again.');
              }
              break;
          }
        }
        return throwError(() => error);
      })
    );
  }
}
